import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Modal, ModalHeader } from './ui';
import { teacherApi } from '../data/api';
import { DOMAINS, SESSION_STATE } from '../data/mockData';
import ViewAsButton from '../../ViewAs/ViewAsButton';
import { rangeFor } from '../../pages/Bluebook/psatReport';

/*
 * StudentOverviewModal (Sandy 2026-08-28): the parent-teacher-conference
 * sheet. One getStudentOverview call — scores (diagnostic, current estimate,
 * goal), domain levels with growth, how much they're working, effort flags
 * with their evidence, and what they keep missing. Opened from the Students
 * tab by clicking a student (or their effort flag).
 */

const BAND_COLOR = { 1: '#a960ee', 2: '#d44995', 3: '#ff333d', 4: '#ff8140', 5: '#f8c12a', 6: '#22c55e', 7: '#60a5fa' };
const bandColor = (b) => BAND_COLOR[b] || '#6b7280';

const SectionLabel = ({ children }) => (
  <div className="text-[10px] font-extrabold uppercase tracking-[0.05em] text-neutral-400 mb-2">{children}</div>
);

const Stat = ({ label, value, sub, quiet }) => (
  <div className="min-w-0">
    <div className={'text-[18px] font-extrabold leading-tight tabular-nums ' + (quiet ? 'text-neutral-300' : 'text-ink')}>{value}</div>
    <div className="text-[11px] font-semibold text-neutral-500 leading-tight mt-0.5">{label}</div>
    {sub && <div className="text-[10.5px] text-neutral-400 leading-tight">{sub}</div>}
  </div>
);

// Same visual language as the Monitor / Intervene flag rows: colored dot +
// SESSION_STATE label.
export const FlagChip = ({ flag }) => {
  const st = SESSION_STATE[flag];
  if (!st) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-ink bg-white border border-neutral-200 rounded-full px-2.5 py-1 whitespace-nowrap">
      <span className="w-2 h-2 rounded-full flex-none" style={{ background: st.dot }} />
      {st.label}
    </span>
  );
};

// What a saved report did to one domain, in the teacher's words.
export function reportEffect(o) {
  if (!o) return 'Still being applied';
  switch (o.result) {
    case 'placed': return `Placed at Level ${o.to}`;
    case 'raised': return `Moved up ${o.from} → ${o.to}${o.restarted ? ' · assignment in progress restarted' : ''}`;
    case 'corrected': return `Changed ${o.from} → ${o.to}`;
    case 'unchanged': return `No change · Level ${o.to}`;
    case 'kept':
      if (o.reason === 'placement_walk') return 'Not used · their short check sets this level';
      if (o.reason === 'practicing') return `No change · practicing at Level ${o.to}`;
      return 'Not applied';
    default: return '';
  }
}

const savedOn = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// PSAT/NMSQT score report (Sandy 2026-10-27): what the student typed in on
// Bluebook / PSAT, newest save first. Self-reported, so it says so. Collapsed
// to one line; opening it shows each domain's reported level and what it did.
export function ScoreReportEntry({ reports }) {
  const [open, setOpen] = useState(false);
  const r = reports[0];
  const byKey = Object.fromEntries((r.outcome || []).map((o) => [o.key, o]));
  return (
    <div className="mt-[12px] rounded-[12px] border-[1px] border-neutral-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-[12px] px-[16px] py-[12px] text-left bg-transparent border-0 rounded-[12px] cursor-pointer hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-bold text-ink">
            PSAT/NMSQT report
            <span className="ml-[6px] inline-block align-[1px] rounded-full bg-neutral-100 px-[8px] text-[10.5px] font-bold leading-[18px] text-neutral-500 whitespace-nowrap">Self-reported</span>
          </div>
          <div className="text-[11.5px] text-neutral-500 tabular-nums">
            {[savedOn(r.submittedAt), `R&W ${r.readingScore}`, `Math ${r.mathScore}`, `Total ${r.totalScore}`].filter(Boolean).join(' · ')}
          </div>
        </div>
        <span className="flex-none text-[12px] font-bold text-ink">{open ? 'Hide' : 'View'}</span>
        <ChevronDown size={16} strokeWidth={2.5} className={'flex-none text-neutral-500 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="border-t border-neutral-100 px-[16px] py-[12px]">
          <p className="mb-0 text-[11.5px] leading-relaxed text-neutral-500">
            The student typed these in from their score report. A report places a domain that has no level yet,
            or moves a placed one up one level at most. It never lowers a level.
          </p>
          <div className="mt-[12px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
            {['math', 'reading'].map((subject) => (
              <div key={subject} className="min-w-0">
                <div className="text-[11.5px] font-extrabold text-neutral-500 mb-1">{subject === 'math' ? 'Math' : 'Reading & Writing'}</div>
                {DOMAINS[subject].map((d) => {
                  const lv = r.levels?.[d.key];
                  return (
                    <div key={d.key} className="flex items-start gap-2 py-1 min-w-0">
                      <span className="flex-none w-6 h-6 rounded-[6px] flex items-center justify-center text-[11px] font-extrabold text-white" style={{ background: bandColor(lv) }}>{lv ?? '?'}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-semibold text-ink truncate">{d.full}</div>
                        <div className="text-[11px] leading-snug text-neutral-500">
                          {lv ? `Reported ${rangeFor(subject, lv)} · ` : ''}{reportEffect(byKey[d.key])}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {reports.length > 1 && (
            <div className="mt-[10px] text-[11px] text-neutral-500">
              Saved {reports.length} times, showing the latest. Earlier: {reports.slice(1).map((x) => `${savedOn(x.submittedAt)} (R&W ${x.readingScore}, Math ${x.mathScore})`).join('; ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function StudentOverviewModal({ classID, auth, student, onClose }) {
  const [ov, setOv] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    teacherApi(auth, 'getStudentOverview', { classID, userId: student.userId, sinceDays: 30 })
      .then((r) => { if (alive) setOv(r); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [auth, classID, student.userId]);

  const pred = ov?.predictedScore || null;
  const predMath = pred?.Math != null ? Number(pred.Math) : null;
  const predRead = pred?.Reading != null ? Number(pred.Reading) : null;
  const diagMath = ov?.diagnostic?.math != null ? Number(ov.diagnostic.math) : null;
  const diagRead = ov?.diagnostic?.reading != null ? Number(ov.diagnostic.reading) : null;
  const a14 = ov?.activity14d || {};
  const work = ov?.work || {};
  const ev = ov?.bucketEvidence || {};
  const accuracy = work.questions ? Math.round((work.correct / work.questions) * 100) : null;

  // Strengths / focus areas from domain levels (1–7 bands).
  const domainRows = ov
    ? ['math', 'reading'].map((subject) => ({
        subject,
        rows: DOMAINS[subject]
          .map((d) => ({ ...d, ...(ov.domains?.[d.key] || {}) }))
          .filter((d) => d.band != null),
      }))
    : [];
  const allBands = domainRows.flatMap((g) => g.rows);
  const sorted = [...allBands].sort((a, b) => b.band - a.band);
  const strengths = sorted.slice(0, 2).filter((d) => d.band >= 4);
  const focus = sorted.slice(-2).filter((d) => d.band <= 4 && !strengths.includes(d));

  const flags = ov?.bucket && ov.bucket !== 'minutes' ? [ov.bucket] : [];

  return (
    <Modal open onClose={onClose} size="lg">
      <ModalHeader
        title={student.name}
        // Email lives here, not in the Students table (Sandy 2026-09-23).
        sub={[student.email, ov?.school, ov?.grade ? `Grade ${ov.grade}` : null].filter(Boolean).join(' · ') || 'Student overview'}
        onClose={onClose}
      >
        {/* View as student (2026-10-27): their real dashboard, read only. Waits
            for the overview (it carries their school), and never inside a view
            (an admin viewing as this teacher). */}
        {ov && auth?.user?.role === 'Teacher' && !auth.user.viewAs && (
          <ViewAsButton
            kind="student"
            viewer="Teacher"
            classId={classID}
            person={{ id: student.userId, name: student.name, email: student.email, grade: ov.grade }}
            school={ov.school || null}
            // Phones: the eye alone (a long name keeps its room); words from sm up.
            labelClassName="hidden sm:inline"
            className="inline-flex items-center justify-center gap-1.5 flex-none font-semibold cursor-pointer rounded-[8px] text-[13px] h-9 w-9 px-[0px] sm:w-auto sm:px-[12px] whitespace-nowrap bg-white hover:bg-neutral-50 active:bg-neutral-100 text-ink border border-neutral-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          />
        )}
      </ModalHeader>
      <div className="px-6 py-[20px] max-h-[72vh] overflow-y-auto">
        {error && <div className="py-8 text-center text-[13px] text-error-700">Couldn&apos;t load this student right now.</div>}
        {!error && !ov && <div className="py-8 text-center text-[13px] text-neutral-500">Loading their overview…</div>}
        {ov && (
          <div className="flex flex-col gap-[20px]">
            {/* scores */}
            <div>
              <SectionLabel>Scores</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-[16px] rounded-[12px] border-[1px] border-neutral-200 bg-neutral-50 px-[16px] py-[12px]">
                <Stat
                  label="Diagnostic"
                  // No total until both sections are scored (2026-09-23):
                  // English can be scored before Math is taken.
                  quiet={diagMath == null || diagRead == null}
                  value={diagMath != null && diagRead != null ? `${diagMath + diagRead}` : '—'}
                  sub={diagMath != null && diagRead != null ? `Math ${diagMath} · R&W ${diagRead}`
                    : diagRead != null ? `R&W ${diagRead} · Math not taken yet`
                    : diagMath != null ? `Math ${diagMath} · R&W not taken yet` : 'Not taken yet'}
                />
                <Stat
                  label="Current estimate"
                  quiet={predMath == null && predRead == null}
                  value={predMath != null && predRead != null ? `≈${predMath + predRead}` : '—'}
                  sub={predMath != null || predRead != null
                    ? `Math ≈${predMath ?? '—'} · R&W ≈${predRead ?? '—'}` : 'Needs more practice data'}
                />
                <Stat
                  label="Goal"
                  quiet={ov.goalScore == null}
                  value={ov.goalScore != null ? ov.goalScore : '—'}
                  sub={ov.goalScore == null ? 'No goal set' : null}
                />
              </div>
            </div>

            {/* how much they're working */}
            <div>
              <SectionLabel>How much they&apos;re working</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-[16px] rounded-[12px] border-[1px] border-neutral-200 bg-neutral-50 px-[16px] py-[12px]">
                <Stat label="This week" value={`${ov.minutesWeek || 0}m`} quiet={!ov.minutesWeek} />
                <Stat label="Last 14 days" value={`${a14.minutes || 0}m`} sub={`${a14.activeDays || 0} of 14 days active`} quiet={!a14.minutes} />
                <Stat
                  label={`Questions · ${work.windowDays || 30}d`}
                  value={work.questions || 0}
                  sub={accuracy != null ? `${accuracy}% correct` : null}
                  quiet={!work.questions}
                />
                <Stat
                  label="Last active"
                  value={a14.lastActiveAt ? new Date(a14.lastActiveAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                  quiet={!a14.lastActiveAt}
                />
              </div>
            </div>

            {/* effort flags — same dot + label language as Monitor/Intervene */}
            <div>
              <SectionLabel>Effort flags · this week</SectionLabel>
              {flags.length === 0 ? (
                <div className="text-[12.5px] text-neutral-500">Nothing looks off this week.</div>
              ) : (
                <div className="rounded-[12px] border-[1px] border-neutral-200 px-[16px] py-[12px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    {flags.map((f) => <FlagChip key={f} flag={f} />)}
                  </div>
                  <div className="mt-2 text-[12px] text-neutral-600 leading-relaxed">
                    {[
                      ev.questions != null ? `${ev.questions} questions this week` : null,
                      ev.avgSecPerQ != null ? `avg ${ev.avgSecPerQ}s per question` : null,
                      ev.copyPaste ? `${ev.copyPaste} copy/paste events` : null,
                      ev.fastItems ? `${ev.fastItems} rushed items` : null,
                      ev.tabAways ? `${ev.tabAways} tab-aways (never counted against them)` : null,
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
              )}
            </div>

            {/* levels per domain, with growth */}
            <div>
              <SectionLabel>Where they are · levels 1–7</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
                {domainRows.map((g) => (
                  <div key={g.subject} className="rounded-[12px] border-[1px] border-neutral-200 px-[16px] py-[12px]">
                    <div className="text-[11.5px] font-extrabold text-neutral-500 mb-2">{g.subject === 'math' ? 'Math' : 'Reading & Writing'}</div>
                    {g.rows.length === 0 && <div className="text-[12px] text-neutral-400">No placement yet.</div>}
                    {g.rows.map((d) => (
                      <div key={d.key} className="flex items-center gap-2 py-1 min-w-0">
                        <span className="flex-none w-6 h-6 rounded-[6px] flex items-center justify-center text-[11px] font-extrabold text-white" style={{ background: bandColor(d.band) }}>{d.band}</span>
                        <span className="text-[12.5px] font-semibold text-ink truncate flex-1">{d.full}</span>
                        {d.initialBand != null && d.band > d.initialBand && (
                          <span className="flex-none text-[11px] font-bold text-success-600" title={`Started at Level ${d.initialBand}`}>+{d.band - d.initialBand}</span>
                        )}
                        {d.stuck && <span className="flex-none text-[10.5px] font-extrabold uppercase text-error-600">stuck</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {(strengths.length > 0 || focus.length > 0) && (
                <div className="mt-2 text-[12px] text-neutral-600 text-pretty">
                  {/* Each domain name stays on one line ("Craft &" / "Structure." read as broken). */}
                  {strengths.length > 0 && <>Strongest: <span className="font-bold">{strengths.map((d, i) => <span key={d.key}>{i ? ', ' : ''}<span className="whitespace-nowrap">{d.full}</span></span>)}</span>. </>}
                  {focus.length > 0 && <>Needs the most work: <span className="font-bold">{focus.map((d, i) => <span key={d.key}>{i ? ', ' : ''}<span className="whitespace-nowrap">{d.full}</span></span>)}</span>.</>}
                </div>
              )}
              {/* Only when they saved one (an older API without the field: nothing). */}
              {Array.isArray(ov.scoreReports) && ov.scoreReports.length > 0 && <ScoreReportEntry reports={ov.scoreReports} />}
            </div>

            {/* what they keep missing */}
            {(work.topMissedSkills || []).length > 0 && (
              <div>
                <SectionLabel>What they keep missing · last {work.windowDays || 30} days</SectionLabel>
                <div className="rounded-[12px] border-[1px] border-neutral-200 divide-y divide-neutral-100">
                  {work.topMissedSkills.slice(0, 6).map((s) => (
                    <div key={s.key} className="flex items-center gap-[12px] px-[16px] py-2">
                      <span className="text-[12.5px] font-semibold text-ink truncate flex-1">{s.label}</span>
                      {s.band != null && <span className="flex-none text-[11px] font-bold text-neutral-400">Level {s.band}</span>}
                      <span className="flex-none text-[12px] font-bold text-error-600 tabular-nums">{s.missed} of {s.attempts} missed</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* unit grades in this class */}
            {(ov.grades || []).length > 0 && (
              <div>
                <SectionLabel>Unit grades in this class</SectionLabel>
                <div className="flex items-center gap-2 flex-wrap">
                  {ov.grades.map((g) => (
                    <span key={g.unitSeq} className="inline-flex items-center gap-1.5 text-[12px] font-bold text-ink bg-neutral-50 border-[1px] border-neutral-200 rounded-[8px] px-2.5 py-1">
                      Unit {g.unitSeq}{g.domainKey ? ` · ${g.domainKey}` : ''}
                      <span className="tabular-nums">{g.final != null ? `${g.final}%` : '—'}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
